import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiShield, FiCoffee, FiSearch, FiClock } = FiIcons;

const TargetGroups = () => {
  const groups = [
    { title: "Pharma & Wirkstoffe", desc: "Höchste Reinheit und Wirkstoffkonstanz für medizinische Anwendungen.", icon: FiShield, color: "bg-emerald-600" },
    { title: "Gourmet & Retail", desc: "Exklusive Kräuter und Microgreens in Gastronomie-Qualität.", icon: FiCoffee, color: "bg-stone-800" },
    { title: "Forschung & Lab", desc: "Reproduzierbare Bedingungen für agrarwissenschaftliche Studien.", icon: FiSearch, color: "bg-emerald-800" }
  ];

  return (
    <div className="grid md:grid-cols-3 gap-6 mt-16">
      {groups.map((group, idx) => (
        <motion.div 
          key={idx} 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: idx * 0.1 }}
          className="bg-white p-8 rounded-3xl border border-stone-100 shadow-sm hover:shadow-xl transition-all duration-300 group"
        >
          <div className={`w-12 h-12 ${group.color} text-white rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
            <SafeIcon icon={group.icon} className="text-xl" />
          </div>
          <h3 className="text-xl font-bold text-stone-900 mb-3">{group.title}</h3>
          <p className="text-stone-500 text-sm leading-relaxed">{group.desc}</p>
        </motion.div>
      ))}
    </div>
  );
};

const Home = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
          <div className="max-w-4xl">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 mb-6">
                <SafeIcon icon={FiClock} className="text-emerald-600 text-sm" />
                <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-[0.2em]">Website im Aufbau</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold leading-[1.05] text-stone-900 mb-8">
                Wir bauen Maschinen, damit Ihre Produktion auf <span className="text-emerald-600 italic">pharmazeutischem Niveau</span> funktioniert.
              </h1>
              <p className="text-xl text-stone-600 mb-10 leading-relaxed max-w-2xl">
                CoreUnit Systems entwickelt modulare Hochtechnologie-Anlagen für kontrollierte Biosysteme. Präzision, Skalierbarkeit und absolute Prozesskontrolle – direkt vom Hub an der A28.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Link 
                  to="/alchemist" 
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-stone-900 hover:bg-black text-white px-8 py-4 rounded-xl font-bold transition-all shadow-xl"
                >
                  Produkt entdecken
                  <SafeIcon icon={FiArrowRight} />
                </Link>
                <Link 
                  to="/anwendungen" 
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-stone-50 text-stone-700 border border-stone-200 px-8 py-4 rounded-xl font-bold transition-all"
                >
                  Einsatzbereiche
                </Link>
              </div>
            </motion.div>

            <TargetGroups />
          </div>
        </div>

        {/* Visual Background Element */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/3 h-2/3 bg-stone-200/50 rounded-l-[100px] -z-10 hidden lg:block overflow-hidden">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/6a4e1632643f5ecd6b4b884d4518abad28ee28269a896d1a9bb8c081180db7d41fc79629d873bafacdf5fb95106d974d/images/aW1hZ2UvcG5n/4c83981534ca2e0e770e4cfaaff2de6c.png" 
            className="w-full h-full object-cover opacity-60 mix-blend-multiply grayscale" 
            alt="Anlagentechnik" 
          />
        </div>
      </section>

      {/* Quick Stats / Location */}
      <section className="py-12 bg-stone-900 border-y border-stone-800">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-center md:justify-between items-center gap-8 opacity-50">
          <span className="font-bold text-white tracking-widest uppercase text-sm">Westerstede Hub</span>
          <span className="font-bold text-white tracking-widest uppercase text-sm">GACP Ready</span>
          <span className="font-bold text-white tracking-widest uppercase text-sm">AI Controlled</span>
          <span className="font-bold text-white tracking-widest uppercase text-sm">Modular Scalability</span>
        </div>
      </section>
    </div>
  );
};

export default Home;